import * as React from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/he";

import { Box, Button, IconButton, Popover, TextField, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { LocalizationProvider, DateCalendar, PickersDay, PickersDayProps } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

type Props = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  value: { from: string; to: string }; // YYYY-MM-DD
  onChange: (next: { from: string; to: string }) => void;
  onApply: () => void;
};

const fmtInput = (v: string) => (v ? dayjs(v).format("DD/MM/YYYY") : "");

function isBetweenInclusive(d: Dayjs, a: Dayjs, b: Dayjs) {
  const x = d.startOf("day").valueOf();
  const min = Math.min(a.startOf("day").valueOf(), b.startOf("day").valueOf());
  const max = Math.max(a.startOf("day").valueOf(), b.startOf("day").valueOf());
  return x >= min && x <= max;
}

type CustomDayExtraProps = {
  from: Dayjs | null;
  to: Dayjs | null;
  today: Dayjs;
};

function CustomDay(props: PickersDayProps<Dayjs> & CustomDayExtraProps) {
  const { day, outsideCurrentMonth, from, to, today, ...other } = props;

  const d = day.startOf("day");
  const isToday = d.isSame(today, "day");

  const isStart = !!from && d.isSame(from, "day");
  const isEnd = !!to && d.isSame(to, "day");
  const inRange = !!from && !!to && isBetweenInclusive(d, from, to);

  const blueSelected = {
    backgroundColor: (theme: any) => `${theme.palette.primary.main} !important`,
    color: (theme: any) => `${theme.palette.primary.contrastText} !important`,
    "&:hover": {
      backgroundColor: (theme: any) => `${theme.palette.primary.dark} !important`,
    },
  } as const;

  return (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      disableMargin
      sx={{
        width: 36,
        height: 36,
        borderRadius: 8,
        position: "relative",

        "&.Mui-focusVisible": { boxShadow: "none" },
        "&:focus-visible": { outline: "none" },

        ...(inRange && {
          backgroundColor: "rgba(63,81,181,0.12) !important",
          borderRadius: 0,
        }),

        ...((isStart || isEnd) ? blueSelected : {}),

        ...(isStart && from && to && {
          borderTopRightRadius: 12,
          borderBottomRightRadius: 12,
        }),
        ...(isEnd && from && to && {
          borderTopLeftRadius: 12,
          borderBottomLeftRadius: 12,
        }),

        ...(isToday && !isStart && !isEnd && {
          outline: "2px solid rgba(63,81,181,0.9)",
          outlineOffset: "-2px",
          borderRadius: 6,
          zIndex: 1,
        }),

        "&.Mui-disabled": { opacity: 0.55 },
      }}
    />
  );
}

export default function DateRangePopover({ open, anchorEl, onClose, value, onChange, onApply }: Props) {
  const today = dayjs().startOf("day");
  const minSelectable = today.subtract(1, "day");

  const from = value.from ? dayjs(value.from).startOf("day") : null;
  const to = value.to ? dayjs(value.to).startOf("day") : null;

  const [baseMonth, setBaseMonth] = React.useState<Dayjs>(() => (from ?? today).startOf("month"));

  React.useEffect(() => {
    if (open) setBaseMonth((from ?? today).startOf("month"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const nextMonth = baseMonth.add(1, "month");

  const shouldDisableDate = (d: Dayjs) => d.startOf("day").isBefore(minSelectable, "day");

  const resetSelection = () => onChange({ from: "", to: "" });

  // ✅ month navigation + reset
  const changeMonth = (delta: number) => {
    resetSelection();
    setBaseMonth(m => m.add(delta, "month"));
  };

  // ✅ shared selection logic
  const applyPickToRange = (picked: Dayjs) => {
    const d = picked.startOf("day");
    if (d.isBefore(minSelectable, "day")) return;

    // 1) first click -> FROM
    if (!from) {
      onChange({ from: d.format("YYYY-MM-DD"), to: "" });
      return;
    }

    // 2) second click -> TO (swap if needed)
    if (from && !to) {
      const start = d.isBefore(from, "day") ? d : from;
      const end = d.isBefore(from, "day") ? from : d;
      onChange({ from: start.format("YYYY-MM-DD"), to: end.format("YYYY-MM-DD") });
      return;
    }

    // 3) restart -> new FROM
    onChange({ from: d.format("YYYY-MM-DD"), to: "" });
  };

  /**
   * ✅ IMPORTANT:
   * If user clicks an "outsideCurrentMonth" day (e.g. April 1 shown inside March grid),
   * we auto-shift baseMonth so the UI matches the chosen date.
   */
  const handlePickBase = (picked: Dayjs | null) => {
    if (!picked) return;
    const d = picked.startOf("day");

    // If picked month != baseMonth month, shift baseMonth to picked month
    if (!d.isSame(baseMonth, "month")) {
      setBaseMonth(d.startOf("month"));
    }

    applyPickToRange(d);
  };

  const handlePickNext = (picked: Dayjs | null) => {
    if (!picked) return;
    const d = picked.startOf("day");

    // If picked month != nextMonth month, shift baseMonth so "nextMonth" becomes picked month
    if (!d.isSame(nextMonth, "month")) {
      setBaseMonth(d.startOf("month").subtract(1, "month"));
    }

    applyPickToRange(d);
  };

  const calendarSx = {
    "& .MuiPickersCalendarHeader-root": { display: "none" },
    "& .MuiPickersCalendarHeader-labelContainer": { display: "none" },
    "& .MuiDayCalendar-weekDayLabel": { fontWeight: 700 },
    "& .MuiDayCalendar-weekContainer": { gap: "6px" },
  } as const;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{
        sx: {
          p: 2,
          width: 650,
          borderRadius: 3,
          direction: "rtl",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
        },
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="he">
        {/* top inputs */}
        <Box display="flex" gap={2} mb={2}>
          <TextField fullWidth label="מתאריך" value={fmtInput(value.from)} InputProps={{ readOnly: true }} />
          <TextField fullWidth label="עד תאריך" value={fmtInput(value.to)} InputProps={{ readOnly: true }} />
        </Box>

        {/* calendars */}
        <Box display="flex" gap={2} alignItems="flex-start">
          {/* baseMonth calendar */}
          <Box sx={{ flex: 1 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <IconButton size="small" onClick={() => changeMonth(-1)}>
                <ChevronRightIcon />
              </IconButton>

              <Typography sx={{ fontWeight: 700 }}>{baseMonth.locale("he").format("MMMM YYYY")}</Typography>

              <IconButton size="small" onClick={() => changeMonth(1)}>
                <ChevronLeftIcon />
              </IconButton>
            </Box>

            <DateCalendar
              key={baseMonth.format("YYYY-MM")}
              value={null}
              onChange={handlePickBase} // ✅ base handler
              referenceDate={baseMonth}
              views={["day"]}
              disableHighlightToday
              showDaysOutsideCurrentMonth
              shouldDisableDate={shouldDisableDate}
              slots={{ day: CustomDay as any }}
              slotProps={{ day: { from, to, today } as any }}
              sx={calendarSx}
            />
          </Box>

          {/* nextMonth calendar */}
          <Box sx={{ flex: 1 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <IconButton size="small" onClick={() => changeMonth(-1)}>
                <ChevronRightIcon />
              </IconButton>

              <Typography sx={{ fontWeight: 700 }}>{nextMonth.locale("he").format("MMMM YYYY")}</Typography>

              <IconButton size="small" onClick={() => changeMonth(1)}>
                <ChevronLeftIcon />
              </IconButton>
            </Box>

            <DateCalendar
              key={nextMonth.format("YYYY-MM")}
              value={null}
              onChange={handlePickNext} // ✅ next handler
              referenceDate={nextMonth}
              views={["day"]}
              disableHighlightToday
              showDaysOutsideCurrentMonth
              shouldDisableDate={shouldDisableDate}
              slots={{ day: CustomDay as any }}
              slotProps={{ day: { from, to, today } as any }}
              sx={calendarSx}
            />
          </Box>
        </Box>

        {/* actions */}
        <Box mt={2} display="flex" justifyContent="flex-start" gap={1}>
          <Button variant="contained" onClick={onApply} disabled={!value.from || !value.to}>
            אישור
          </Button>
          <Button variant="text" onClick={onClose}>
            ביטול
          </Button>
        </Box>
      </LocalizationProvider>
    </Popover>
  );
}